// v49.10.8 — Account setup prompt.
// Renders when a signed-in user reaches a protected route with no
// companyId. Most commonly: signups that ran while the Supabase
// GoTrue lock was hanging (v49.10.0-v49.10.5) — their profile upsert
// and create_company_for_user RPC both silently failed, leaving an
// auth.users row with no profile + no membership.
//
// On submit: upserts the profile + calls the idempotent
// create_company_for_user RPC + reloads so AuthProvider re-hydrates
// from the now-populated tables. Checks for an existing membership first
// and reuses it rather than creating a new company every time — see
// AuthContext.tsx's hydrate_error state for the actual fix to the
// "shown repeatedly despite already being set up" bug this guards against.

import { useState } from "react";
import { useAuth } from "../AuthContext";
import { supabase } from "@/lib/api/supabase-client";
import { LABOURCORE_API } from "@/lib/api";
import type { Role } from "@/lib/api/types";

const ROLE_OPTIONS: Array<{ value: Role; label: string; blurb: string }> = [
  { value: "coordinator", label: "Coordinator", blurb: "I run MEP packages and need to find labour." },
  { value: "partner",     label: "Partner",     blurb: "I supply MEP crews to coordinators." },
  { value: "hybrid",      label: "Hybrid",      blurb: "Both — we coordinate AND supply." },
];

export function AccountSetupPrompt() {
  const { rawSession } = useAuth();
  const email = rawSession?.user?.email || "";
  const metaName = (rawSession?.user?.user_metadata?.name as string | undefined) || "";

  const [name, setName]       = useState(metaName);
  const [company, setCompany] = useState("");
  const [role, setRole]       = useState<Role>("coordinator");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const canSubmit = !!name.trim() && !!company.trim() && !busy;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit || !rawSession?.user) return;
    setBusy(true);
    setError(null);
    try {
      const userId = rawSession.user.id;
      const trimmedName    = name.trim();
      const trimmedCompany = company.trim();

      /* Defense-in-depth against the "loop forever" bug: this screen
         should now only render when hydration genuinely found no
         membership (see AuthContext.tsx's hydrate_error state), but
         double-check right before creating anything — if a membership
         already exists (e.g. it landed just after this screen rendered),
         reuse it instead of creating yet another company. */
      const { data: existing } = await supabase
        .from("memberships")
        .select("company_id")
        .eq("user_id", userId)
        .limit(1);
      const existingCompanyId = (existing as Array<{ company_id: string }> | null)?.[0]?.company_id;
      if (existingCompanyId) {
        LABOURCORE_API.setActiveCompanyId(existingCompanyId);
        window.location.reload();
        return;
      }

      const { error: upsertErr } = await supabase.from("profiles").upsert({
        id:       userId,
        email,
        name:     trimmedName,
        role,
        company:  trimmedCompany,
        title:    role === "coordinator" ? "MEP Project Manager" : "",
        location: "London",
      });
      if (upsertErr) throw new Error("Profile save failed: " + upsertErr.message);

      const kind = role === "coordinator" ? "coordinator" : role === "partner" ? "partner" : "hybrid";
      const newCompanyId = await LABOURCORE_API.companies.createForUser(trimmedCompany, kind);
      if (!newCompanyId) throw new Error("Could not create company — see console for details.");

      /* Full reload so AuthProvider re-hydrates from fresh profile + memberships. */
      window.location.reload();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-lc-navy to-lc-blue-dark p-5">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="inline-flex w-12 h-12 rounded-xl bg-lc-blue-light text-lc-blue items-center justify-center mb-3">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" />
            </svg>
          </div>
          <h1 className="text-xl font-extrabold text-lc-ink">Finish setting up your account</h1>
          <p className="text-sm text-lc-mute mt-1">
            Your sign-up didn't quite complete — we need a few details to create your workspace.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="Your name" value={name} onChange={setName} placeholder="Jane Smith" autoComplete="name" />
          <Field label="Company name" value={company} onChange={setCompany} placeholder="Acme MEP Ltd" autoComplete="organization" />

          <div>
            <span className="block text-[10px] font-extrabold tracking-widest uppercase text-lc-mute mb-2">Role</span>
            <div className="grid gap-2">
              {ROLE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-lc-md border cursor-pointer transition-colors ${
                    role === opt.value ? "border-lc-blue bg-lc-blue-light" : "border-lc-line hover:bg-lc-bg-2"
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={opt.value}
                    checked={role === opt.value}
                    onChange={() => setRole(opt.value)}
                    className="mt-0.5 accent-lc-blue"
                  />
                  <div>
                    <div className="text-sm font-bold text-lc-ink">{opt.label}</div>
                    <div className="text-xs text-lc-mute">{opt.blurb}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="px-3 py-2 rounded-lc-sm bg-lc-red-bg text-lc-red-fg text-xs leading-snug border border-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`w-full py-3 rounded-lc-md font-bold text-sm text-white transition-colors ${
              canSubmit
                ? "bg-gradient-to-br from-lc-blue to-lc-blue-dark hover:shadow-lg cursor-pointer"
                : "bg-lc-line cursor-not-allowed"
            }`}
          >
            {busy ? "Setting up…" : "Create my workspace"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, placeholder, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] font-extrabold tracking-widest uppercase text-lc-mute">{label}</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="px-3 py-2.5 rounded-lc-md border border-lc-line bg-white text-sm text-lc-ink placeholder:text-lc-faint outline-none focus:border-lc-blue focus:ring-2 focus:ring-lc-blue-light"
      />
    </label>
  );
}
